/** Case management system detection via domains, headers, email patterns, and content. */

import { extractDomain } from './fingerprints';
import { extractEmailFromFromField } from '../gmail/message-processing-helpers';

// Known case management system domains
const CASE_MANAGEMENT_DOMAINS = [
  'zendesk.com',
  'freshdesk.com',
  'servicenow.com',
  'jira.com', // Jira Service Management
  'atlassian.com', // Atlassian Service Management
  'intercom.com',
  'helpscout.com',
  'uservoice.com',
  'crisp.chat',
  'drift.com',
  'liveagent.com',
  'kayako.com',
  'zoho.com', // Zoho Desk
  'salesforce.com', // Salesforce Service Cloud
  'microsoft.com', // Microsoft Dynamics, Teams support
  'freshservice.com',
  'happyfox.com',
  'osticket.com',
  'osticket.org',
  'redmine.org',
  'redmine.com',
  'mantisbt.org',
  'bugzilla.org',
  'supportbee.com',
  'groovehq.com',
  'tawk.to',
  'olark.com',
  'livechat.com',
  'livechatinc.com',
  'chatlio.com',
  'tawk.to',
  'purechat.com',
];

// Email address patterns that indicate case management systems
const CASE_MANAGEMENT_EMAIL_PATTERNS = [
  /^support@/i,
  /^help@/i,
  /^ticket@/i,
  /^case@/i,
  /^noreply@/i,
  /^no-reply@/i,
  /^donotreply@/i,
  /^do-not-reply@/i,
  /^notifications@/i,
  /^alerts@/i,
  /^system@/i,
  /^automated@/i,
  /^service@/i,
  /^customer-service@/i,
  /^customerservice@/i,
  /^helpdesk@/i,
  /^help-desk@/i,
];

// Headers that indicate case management systems
const CASE_MANAGEMENT_HEADERS = [
  'X-Ticket-ID',
  'X-Ticket-Id',
  'X-Case-ID',
  'X-Case-Id',
  'X-Request-ID',
  'X-Request-Id',
  'X-Support-ID',
  'X-Support-Id',
  'X-Zendesk-Ticket-ID',
  'X-Zendesk-Ticket-Id',
  'X-Freshdesk-Ticket-ID',
  'X-Freshdesk-Ticket-Id',
  'X-Servicenow-Ticket-ID',
  'X-Servicenow-Ticket-Id',
  'X-Jira-Issue-Key',
  'X-Intercom-Conversation-ID',
  'X-Intercom-Conversation-Id',
];

// Subject line patterns that indicate case management systems
const CASE_MANAGEMENT_SUBJECT_PATTERNS = [
  /\[Ticket\s*#?\s*\d+\]/i,
  /\[Case\s*#?\s*\d+\]/i,
  /\[Request\s*#?\s*\d+\]/i,
  /\[Issue\s*#?\s*\d+\]/i,
  /\[Support\s*#?\s*\d+\]/i,
  /Ticket\s*#?\s*\d+/i,
  /Case\s*#?\s*\d+/i,
  /Request\s*#?\s*\d+/i,
  /Issue\s*#?\s*\d+/i,
  /Support\s*#?\s*\d+/i,
  /Re:\s*\[Ticket/i,
  /Re:\s*\[Case/i,
  /Re:\s*\[Support/i,
];

// From name patterns that indicate case management systems
const CASE_MANAGEMENT_FROM_NAME_PATTERNS = [
  /^Support$/i,
  /^Help Desk$/i,
  /^Customer Service$/i,
  /^Customer Support$/i,
  /^Technical Support$/i,
  /^Service Desk$/i,
  /^Help Center$/i,
  /^Support Team$/i,
  /^Support Center$/i,
  /^Support System$/i,
  /^Automated Support$/i,
  /^System$/i,
  /^Notification$/i,
  /^Alert$/i,
];

export interface CaseManagementDetails {
  detected: boolean;
  confidence: 'high' | 'medium' | 'low';
  indicators: {
    domain_match: boolean;
    email_pattern_match: boolean;
    header_match: boolean;
    subject_pattern_match: boolean;
    from_name_pattern_match: boolean;
  };
  matched_domain: string | null;
  matched_email_pattern: string | null;
  matched_header: string | null;
  matched_subject_pattern: string | null;
  matched_from_name_pattern: string | null;
}

export function checkCaseManagementSystem(
  headers: Record<string, string>,
  fromEmail: string,
  fromName: string | null,
  subject: string
): [number, string[]] {
  /**
   * Check if email is from a case management system.
   * Subtracts points (reduces spam score) as these are legitimate support emails.
   *
   * @param headers - Email headers.
   * @param fromEmail - From email address.
   * @param fromName - From name (display name).
   * @param subject - Email subject.
   * @returns Tuple of (score_reduction, reasons).
   */
  const details = checkCaseManagementSystemWithDetails(headers, fromEmail, fromName, subject);

  if (!details.detected) {
    return [0, []];
  }

  // Subtract points based on confidence level
  // High confidence = -5 points (strong trust signal)
  // Medium confidence = -3 points (moderate trust signal)
  // Low confidence = -2 points (weak trust signal)
  let scoreReduction = 0;
  let reason = '';

  if (details.confidence === 'high') {
    scoreReduction = 5;
    reason = 'Case management system detected (high confidence) - trust signal';
  } else if (details.confidence === 'medium') {
    scoreReduction = 3;
    reason = 'Case management system detected (medium confidence) - trust signal';
  } else {
    scoreReduction = 2;
    reason = 'Case management system detected (low confidence) - trust signal';
  }

  return [scoreReduction, [reason]];
}

export function checkCaseManagementSystemWithDetails(
  headers: Record<string, string>,
  fromEmail: string,
  fromName: string | null,
  subject: string
): CaseManagementDetails {
  /**
   * Check if email is from a case management system with detailed results.
   *
   * @param headers - Email headers.
   * @param fromEmail - From email address.
   * @param fromName - From name (display name).
   * @param subject - Email subject.
   * @returns Detailed case management detection information.
   */
  const result: CaseManagementDetails = {
    detected: false,
    confidence: 'low',
    indicators: {
      domain_match: false,
      email_pattern_match: false,
      header_match: false,
      subject_pattern_match: false,
      from_name_pattern_match: false,
    },
    matched_domain: null,
    matched_email_pattern: null,
    matched_header: null,
    matched_subject_pattern: null,
    matched_from_name_pattern: null,
  };

  // fromEmail may be the raw "From" header value (e.g. `"PayPal Support" <spammer@evil.com>`),
  // not a bare address. Extract the actual address before running domain/local-part checks
  // against it — matching patterns against the raw header string would let an attacker's
  // display name spoof the local-part patterns below regardless of the real sending domain.
  const bareEmail = extractEmailFromFromField(fromEmail);

  // Check domain match
  const domain = extractDomain(bareEmail);
  if (domain) {
    const domainLower = domain.toLowerCase();
    for (const caseDomain of CASE_MANAGEMENT_DOMAINS) {
      if (domainLower === caseDomain || domainLower.endsWith(`.${caseDomain}`)) {
        result.indicators.domain_match = true;
        result.matched_domain = caseDomain;
        break;
      }
    }
  }

  // Check email pattern match (against the parsed bare address, not the raw header)
  for (const pattern of CASE_MANAGEMENT_EMAIL_PATTERNS) {
    if (pattern.test(bareEmail)) {
      result.indicators.email_pattern_match = true;
      result.matched_email_pattern = pattern.toString();
      break;
    }
  }

  // Check header match
  const headerKeys = Object.keys(headers).map((h) => h.toLowerCase());
  for (const headerName of CASE_MANAGEMENT_HEADERS) {
    const headerNameLower = headerName.toLowerCase();
    if (headerKeys.includes(headerNameLower)) {
      result.indicators.header_match = true;
      result.matched_header = headerName;
      break;
    }
  }

  // Check subject pattern match
  for (const pattern of CASE_MANAGEMENT_SUBJECT_PATTERNS) {
    if (pattern.test(subject)) {
      result.indicators.subject_pattern_match = true;
      result.matched_subject_pattern = pattern.toString();
      break;
    }
  }

  // Check from name pattern match
  if (fromName) {
    for (const pattern of CASE_MANAGEMENT_FROM_NAME_PATTERNS) {
      if (pattern.test(fromName)) {
        result.indicators.from_name_pattern_match = true;
        result.matched_from_name_pattern = pattern.toString();
        break;
      }
    }
  }

  // Determine confidence and detection
  const indicatorCount = Object.values(result.indicators).filter(Boolean).length;

  if (indicatorCount === 0) {
    result.detected = false;
    return result;
  }

  result.detected = true;

  // High confidence requires a domain_match against a known case-management vendor domain.
  // email_pattern_match, header_match, and subject_pattern_match are all derived from
  // attacker-controllable content (From local-part, headers, subject) with no verification
  // that the mail actually originated from a real case-management vendor, so they must not
  // combine into 'high' confidence without domain corroboration. An unrecognized domain
  // still falls through to 'medium' below via the existing indicatorCount >= 2 rule.
  if (result.indicators.domain_match) {
    result.confidence = 'high';
  }
  // Medium confidence: 2+ indicators (but not high confidence combo)
  else if (indicatorCount >= 2) {
    result.confidence = 'medium';
  }
  // Low confidence: single indicator
  else {
    result.confidence = 'low';
  }

  return result;
}
